import { Link, useRouterState } from "@tanstack/react-router";

import { useCan } from "@/lib/auth/useCan";
import { cn } from "@/lib/utils";

import type { CroForecastSearch } from "../search";

const TABS: { to: string; label: string; admin?: boolean; write?: boolean }[] = [
  { to: "/cro-forecast/summary", label: "Summary" },
  { to: "/cro-forecast/weekly-kpi", label: "Weekly KPI" },
  { to: "/cro-forecast/forecast-meeting", label: "Forecast Meeting" },
  { to: "/cro-forecast/pipeline-movement", label: "Pipeline Movement" },
  { to: "/cro-forecast/top-of-funnel", label: "Top of Funnel" },
  { to: "/cro-forecast/deals", label: "Open Deals" },
  { to: "/cro-forecast/deal-health", label: "Deal Health" },
  { to: "/cro-forecast/reconcile-deals", label: "Reconcile Deals", write: true },
  { to: "/cro-forecast/how-to", label: "How To", write: true },
  { to: "/cro-forecast/admin", label: "Admin", admin: true },
];

/**
 * Tabs across the top — Summary / Weekly KPI / Forecast Meeting / Pipeline
 * Movement / Top of Funnel / Open Deals / How To / Admin. The shared
 * date-filter search params are preserved as the user switches tabs (each
 * Link carries the current search as-is). The How To tab is only rendered
 * for users with cro_forecast.write; Admin only for cro_forecast.admin.
 */
export function CroForecastSubNav({ search }: { search?: CroForecastSearch }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const canAdmin = useCan("cro_forecast.admin");
  const canWrite = useCan("cro_forecast.write");
  return (
    // Sticks directly below the FilterBar (which sits sticky at top-0,
    // ~58px tall). z-10 matches the FilterBar so neither overlays the
    // other, and bg-card keeps scrolled content from showing through.
    <nav className="sticky top-[58px] z-10 flex gap-1 overflow-x-auto border-b bg-card">
      {TABS.filter((t) => {
        if (t.admin) return canAdmin;
        if (t.write) return canWrite;
        return true;
      }).map((t) => (
        <SubLink
          key={t.to}
          to={t.to}
          active={path.startsWith(t.to)}
          search={search}
        >
          {t.label}
        </SubLink>
      ))}
    </nav>
  );
}

function SubLink({
  to,
  children,
  active,
  search,
}: {
  to: string;
  children: React.ReactNode;
  active: boolean;
  search?: CroForecastSearch;
}) {
  return (
    <Link
      to={to}
      search={search as Record<string, unknown> | undefined}
      className={cn(
        "shrink-0 px-4 py-2 text-sm font-medium",
        active
          ? "border-b-2 border-power text-foreground"
          : "border-b-2 border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </Link>
  );
}
